import type { AssetStore } from '../assets/loader';
import { effectiveVolume, onAudioPreferenceChange } from './preferences';
import {
  AudioStateTracker,
  SOUND,
  type AudioFrame,
  type AudioMatchView,
  type SoundKey,
} from './state';

const SOUND_KEYS = Object.values(SOUND);

/**
 * Fixed low ambience the crowd ducks to for the pause menu, independent of whatever excitement
 * the match was at the moment pause was pressed — a goal's roar and a dead 0-0 both settle on
 * the same quiet murmur rather than either freezing loud or popping silent.
 */
const PAUSE_MENU_CROWD_GAIN = 0.04;

/** How long ducking into the pause menu, and climbing back out of it, takes. */
const PAUSE_RAMP_SECONDS = 0.3;

/**
 * Whether a real, trusted key or pointer gesture has happened anywhere on the page yet.
 *
 * A gamepad button press can never set this — browsers do not treat Gamepad API polling as a
 * user gesture for autoplay purposes, so there is no trusted event to catch. Marked from the
 * shell's own global input handling (`src/ui/shell.ts`), independent of whether a `MatchAudio`
 * instance exists yet, since one is only created once a match actually starts. A host that
 * knows this is already true by the time it builds a `MatchAudio` can call `resume()` on it
 * immediately instead of waiting for a second gesture that a gamepad-only session may never
 * produce again.
 */
let earlyGestureSeen = false;

export function markEarlyAudioGesture(): void {
  earlyGestureSeen = true;
}

export function hasEarlyAudioGesture(): boolean {
  return earlyGestureSeen;
}

/** Test-only: this module's state is otherwise a real page's whole-lifetime record. */
export function resetEarlyAudioGesture(): void {
  earlyGestureSeen = false;
}

export interface MatchAudioOptions {
  /**
   * Applied to every source gain, never as a listener gain. Defaults to whatever the person
   * last chose on the SETTINGS page, which is the original's own half until he moves it.
   */
  masterVolume?: number;
  /** Primarily for deterministic tests. */
  random?: () => number;
  /** A dedicated context can be supplied by a host that already owns browser audio. */
  context?: AudioContext;
  /** Defaults to `window`; null disables automatic gesture listeners. */
  gestureTarget?: EventTarget | null;
}

/**
 * Fully resident match audio.
 *
 * Call `update(match)` after simulation ticks or once per rendered frame. Repeated calls at
 * the same match time are harmless. The context starts suspended and this class resumes it
 * on the first pointer or keyboard gesture; `resume()` is also public for hosts with a
 * central gesture handler.
 */
export class MatchAudio {
  private readonly context: AudioContext;
  private readonly tracker: AudioStateTracker;
  private readonly buffers = new Map<SoundKey, AudioBuffer>();
  private readonly crowdGains = new Map<SoundKey, GainNode>();
  private gestureTarget: EventTarget | null = null;
  private resumePending: Promise<void> | null = null;
  private loadPending: Promise<void> | null = null;
  private stopListening: (() => void) | null = null;
  /** Nothing is played at all while this is zero, rather than played at a gain of nothing. */
  private volume: number;

  private constructor(options: MatchAudioOptions) {
    const Context = globalThis.AudioContext;
    this.context = options.context ?? new Context();
    this.volume = Math.min(Math.max(options.masterVolume ?? effectiveVolume(), 0), 1);
    this.tracker = new AudioStateTracker(this.volume, options.random ?? Math.random);
  }

  /**
   * Create the suspended context and arm the gesture listeners. Call this during page startup,
   * before the first interaction; audio assets can be attached later with `load(store)`.
   */
  static init(options: MatchAudioOptions = {}): MatchAudio {
    const audio = new MatchAudio(options);
    const target =
      options.gestureTarget === undefined
        ? (typeof window === 'undefined' ? null : window)
        : options.gestureTarget;
    audio.installGestureResume(target);
    // Only when the host has not pinned a loudness of its own. One that has is answering for
    // it — a test, or a viewer that wants a known mix — and the menus must not move it.
    if (options.masterVolume === undefined) {
      audio.stopListening = onAudioPreferenceChange((preference) => {
        audio.setVolume(effectiveVolume(preference));
      });
    }
    return audio;
  }

  /**
   * Change how loud everything is, taking effect on the spot. Zero is silence: nothing is
   * started at all, rather than started at a gain of nothing.
   */
  setVolume(volume: number): void {
    this.volume = Math.min(Math.max(volume, 0), 1);
    if (this.crowdGains.size > 0) this.setCrowdGains(this.tracker.setMasterVolume(this.volume));
    else this.tracker.setMasterVolume(this.volume);
  }

  /**
   * Duck the crowd for the pause menu, or lift the duck back off.
   *
   * The tick loop stops calling `update()` at all the instant pause is pressed (`src/main.ts`),
   * so with nothing else touching them the crowd's gain nodes hold at whatever they were doing
   * the moment the menu went up — mid-roar after a goal, or dead flat at 0-0. This ramps the
   * two crowd layers directly to a fixed, quiet menu level rather than teaching the excitement
   * tracker anything about pause; lifting it ramps back to `setMasterVolume`'s own reading of
   * the excitement right now, which is not necessarily what was playing when pause was pressed.
   */
  setPaused(paused: boolean): void {
    if (this.crowdGains.size === 0) return;
    const until = this.context.currentTime + PAUSE_RAMP_SECONDS;
    if (paused) {
      const ducked = PAUSE_MENU_CROWD_GAIN * this.volume;
      for (const gain of this.crowdGains.values()) gain.gain.linearRampToValueAtTime(ducked, until);
    } else {
      const frame = this.tracker.setMasterVolume(this.volume);
      this.crowdGains.get(SOUND.quietCrowd)!.gain.linearRampToValueAtTime(frame.quietCrowdGain, until);
      this.crowdGains.get(SOUND.loudCrowd)!.gain.linearRampToValueAtTime(frame.loudCrowdGain, until);
    }
  }

  /** Let go of the menu subscription. A page keeps one of these for its whole life. */
  dispose(): void {
    this.stopListening?.();
    this.stopListening = null;
  }

  /** Fetch and fully decode all six match sounds. Safe to call more than once. */
  load(store: AssetStore): Promise<void> {
    if (!this.loadPending) {
      this.loadPending = this.loadBuffers(store).then(() => this.startCrowd());
    }
    return this.loadPending;
  }

  /**
   * Resume the suspended context. Browser policy requires this call to begin inside a user
   * gesture; the automatic listeners installed by `init` satisfy that requirement.
   */
  resume(): Promise<void> {
    if (this.context.state === 'running') {
      this.removeGestureResume();
      return Promise.resolve();
    }
    if (!this.resumePending) {
      this.resumePending = this.context.resume().then(() => {
        this.resumePending = null;
        if (this.context.state === 'running') this.removeGestureResume();
      }, (error: unknown) => {
        this.resumePending = null;
        throw error;
      });
    }
    return this.resumePending;
  }

  /**
   * Between matches: forget the one just played and take the crowd back down to nothing.
   *
   * The loops themselves keep running, because starting and stopping a looping source is how
   * you get a click; only their gains move.
   */
  reset(): void {
    this.tracker.reset();
    const now = this.context.currentTime;
    for (const gain of this.crowdGains.values()) gain.gain.setValueAtTime(0, now);
  }

  /**
   * The one per-tick/per-frame entry point.
   *
   * The tracker is asked even in silence, because every sound it decides on is a comparison
   * against the last tick — the clock, the score, the last man to touch the ball. Skip the
   * call while the sound is off and turning it back on mid-match blows the whistle for a foul
   * given ten minutes ago.
   */
  update(match: AudioMatchView): void {
    const frame = this.tracker.update(match);
    if (this.volume > 0) {
      for (const shot of frame.oneShots) {
        this.playOneShot(shot.key, shot.gain, shot.playbackRate);
      }
    }
    this.setCrowdGains(frame);
  }

  private async loadBuffers(store: AssetStore): Promise<void> {
    await Promise.all(
      SOUND_KEYS.map(async (key) => {
        const encoded = await store.audio(key);
        const decoded = await this.context.decodeAudioData(encoded.slice(0));
        const durationMs = store.manifest.audio[key]!.durationMs;
        this.buffers.set(key, trimLeadingDecoderDelay(this.context, decoded, durationMs));
      }),
    );
  }

  private startCrowd(): void {
    for (const key of [SOUND.quietCrowd, SOUND.loudCrowd]) {
      const source = this.context.createBufferSource();
      const gain = this.context.createGain();
      source.buffer = this.buffers.get(key)!;
      source.loop = true;
      gain.gain.value = 0;
      source.connect(gain).connect(this.context.destination);
      source.start();
      this.crowdGains.set(key, gain);
    }
  }

  /**
   * A fresh source per trigger is deliberate. The original permanently bound one source to
   * each buffer, so a rapid second contact cut off the first. Web Audio sources are cheap and
   * allowing the hits to overlap preserves both contacts.
   */
  private playOneShot(key: SoundKey, gainValue: number, playbackRate: number): void {
    const source = this.context.createBufferSource();
    const gain = this.context.createGain();
    source.buffer = this.buffers.get(key)!;
    source.playbackRate.value = playbackRate;
    gain.gain.value = gainValue;
    source.connect(gain).connect(this.context.destination);
    source.addEventListener('ended', () => {
      source.disconnect();
      gain.disconnect();
    }, { once: true });
    source.start();
  }

  private setCrowdGains(frame: AudioFrame): void {
    const now = this.context.currentTime;
    this.crowdGains.get(SOUND.quietCrowd)!.gain.setValueAtTime(frame.quietCrowdGain, now);
    this.crowdGains.get(SOUND.loudCrowd)!.gain.setValueAtTime(frame.loudCrowdGain, now);
  }

  private installGestureResume(target: EventTarget | null): void {
    if (!target || this.context.state === 'running') return;
    this.gestureTarget = target;
    target.addEventListener('pointerdown', this.onFirstGesture, { capture: true });
    target.addEventListener('keydown', this.onFirstGesture, { capture: true });
  }

  private removeGestureResume(): void {
    this.gestureTarget?.removeEventListener('pointerdown', this.onFirstGesture, { capture: true });
    this.gestureTarget?.removeEventListener('keydown', this.onFirstGesture, { capture: true });
    this.gestureTarget = null;
  }

  private readonly onFirstGesture = (): void => {
    void this.resume().catch((error: unknown) => {
      console.warn('[audio] browser refused to resume the audio context', error);
    });
  };
}

function trimLeadingDecoderDelay(
  context: BaseAudioContext,
  decoded: AudioBuffer,
  durationMs: number,
): AudioBuffer {
  const expectedFrames = Math.round((durationMs / 1000) * decoded.sampleRate);
  if (decoded.length <= expectedFrames) return decoded;

  const extraFrames = decoded.length - expectedFrames;
  const trimmed = context.createBuffer(
    decoded.numberOfChannels,
    expectedFrames,
    decoded.sampleRate,
  );
  for (let channel = 0; channel < decoded.numberOfChannels; channel++) {
    trimmed.copyToChannel(decoded.getChannelData(channel).subarray(extraFrames), channel);
  }
  return trimmed;
}
